import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FolderKanban, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loader, EmptyState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProjectStats, attention } from "@/components/app/ProjectStats";

export const PSTATUS = { planning: "bg-muted text-muted-foreground", active: "bg-health/15 text-health", complete: "bg-navy/10 text-foreground", archived: "bg-muted text-muted-foreground" };
const EMPTY = { name: "", client: "", site_address: "", start_date: "", end_date: "", scope: "" };

export const ProjectForm = ({ init = EMPTY, submit, busy, label }) => {
  const [f, setF] = useState(init);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); submit(f); }}>
      <div className="space-y-1"><Label>Project name</Label><Input required minLength={3} value={f.name} onChange={set("name")} placeholder="Office refurbishment — 12 High Street" data-testid="project-name" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label>Client</Label><Input value={f.client} onChange={set("client")} data-testid="project-client" /></div>
        <div className="space-y-1"><Label>Site address</Label><Input value={f.site_address} onChange={set("site_address")} data-testid="project-site" /></div>
        <div className="space-y-1"><Label>Start date</Label><Input type="date" value={f.start_date || ""} onChange={set("start_date")} data-testid="project-start" /></div>
        <div className="space-y-1"><Label>End date</Label><Input type="date" value={f.end_date || ""} onChange={set("end_date")} data-testid="project-end" /></div>
      </div>
      <div className="space-y-1"><Label>Scope of works</Label><Textarea required minLength={20} rows={6} value={f.scope} onChange={set("scope")} placeholder="Full refurbishment of a two-storey commercial office including strip-out, electrical alterations, decorating and replacement floor finishes." data-testid="project-scope" />
        <p className="text-xs text-muted-foreground">Describe the work once — Saflr reuses these facts across every document in the pack.</p></div>
      <Button type="submit" className="w-full" disabled={busy} data-testid="project-submit">{busy ? "Saving…" : label}</Button>
    </form>
  );
};

const Totals = ({ items }) => {
  const sum = (f) => items.reduce((a, p) => a + f(p.stats), 0);
  const cells = [["Active projects", items.filter((p) => p.status !== "complete").length, ""], ["Drafts to review", sum((s) => s.documents.drafts + s.documents.under_review), "text-[#B87300] dark:text-[#FFA000]"],
    ["Live permits", sum((s) => s.permits.live), "text-health"], ["Suspended permits", sum((s) => s.permits.suspended), "text-alert"], ["Open actions", sum((s) => s.actions.open), ""], ["Overdue actions", sum((s) => s.actions.overdue), "text-alert"]];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="projects-totals">
      {cells.map(([l, v, c]) => <div key={l} className="rounded-xl border bg-card p-4"><p className="text-xs text-muted-foreground">{l}</p><p className={`mt-1 font-display text-2xl font-bold ${v ? c : ""}`} data-testid={`total-${l.toLowerCase().replace(/ /g, "-")}`}>{v}</p></div>)}
    </div>
  );
};

export default function Projects() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/projects").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const create = async (f) => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects", { ...f, start_date: f.start_date || null, end_date: f.end_date || null });
      nav(`/app/projects/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Projects</h1><p className="text-sm text-muted-foreground">Describe a project once and Saflr drafts a linked pack of RAMS, permits and inspection checklists.</p></div>
        <Button onClick={() => setOpen(true)} data-testid="new-project-btn"><Plus className="mr-1.5 h-4 w-4" />New project</Button>
      </div>
      {!items ? <Loader /> : !items.length ? <EmptyState icon={FolderKanban} title="No projects yet" text="Create a project from a scope of works to generate a draft HSE pack." /> : (
        <>
        <Totals items={items} />
        <div className="grid gap-4 md:grid-cols-2">{[...items].sort((a, b) => attention(b.stats) - attention(a.stats)).map((p) => (
          <Link key={p.id} to={`/app/projects/${p.id}`} className="space-y-3 rounded-2xl border bg-card p-5 transition-colors hover:border-health/60" data-testid={`project-${p.ref}`}>
            <div>
              <div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{p.ref}</span><span className={`rounded-full px-2 py-0.5 text-xs capitalize ${PSTATUS[p.status]}`}>{p.status}</span>{(p.stats.actions.overdue > 0 || p.stats.permits.suspended > 0) && <span className="rounded-full bg-alert/15 px-2 py-0.5 text-xs font-medium text-alert">Needs attention</span>}</div>
              <p className="mt-1 font-semibold">{p.name}</p>
              <p className="text-sm text-muted-foreground">{[p.client, p.site_address].filter(Boolean).join(" · ") || "No client or site yet"}</p>
            </div>
            <ProjectStats s={p.stats} compact />
            <p className="text-xs text-muted-foreground">{p.stats.pack.proposed ? `HSE pack: ${p.stats.pack.created} of ${p.stats.pack.proposed} items created${p.stats.pack.generating ? ` · ${p.stats.pack.generating} drafting` : ""}` : "HSE pack not proposed yet"}</p>
          </Link>))}</div>
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-xl"><DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader><ProjectForm submit={create} busy={busy} label="Create project" /></DialogContent></Dialog>
    </div>
  );
}
